'use client';

import React, { createContext, useState, useContext, useCallback, useEffect, startTransition, useRef } from 'react';
import isEqual from 'lodash/isEqual';
import { useRouter } from 'next/router';
import { getContainer } from '@framework/di/getContainer';
import BreadcrumbsRepository from '@/creditflowresearch/infrastructure/repository/BreadcrumbsRepository';
import { AlertManager } from '@creditflowresearch/uilib/Alert';
import { getErrorMessage } from '@framework/message/Mapper/getMessage';
import getIssuerBreadcrumbs from '@/creditflowresearch/ui/components/issuer/getIssuerBreadcrumbs';
import IBreadcrumb from '@/creditflowresearch/domain/vo/Breadcrumbs';
import { useMounted } from '@/creditflowresearch/ui/common/hooks';

const useBreadcrumbs = (serverBreadcrumbs) => {
  const container = getContainer();
  const [breadcrumbs, setBreadcrumbs] = useState<IBreadcrumb[]>(serverBreadcrumbs);
  const requestService = container.get(BreadcrumbsRepository);
  const { query } = useRouter();
  const isMounted = useMounted();
  const previousServerBreadcrumbsRef = useRef(serverBreadcrumbs);

  const getIssuerBreadcrumbsReq = useCallback(async (companyViewId) => {
    try {
      const data = await requestService.getBreadcrumbs(companyViewId);
      startTransition(() => {
        setBreadcrumbs(getIssuerBreadcrumbs(query, data.parent, data.issuer));
      });
    } catch (e) {
      AlertManager.error(getErrorMessage(e));
    }
  }, [query, requestService]);

  useEffect(() => {
    if (!isMounted) {
      return;
    }

    if (serverBreadcrumbs?.hasError) {
      getIssuerBreadcrumbsReq(query.companyViewId);
      previousServerBreadcrumbsRef.current = serverBreadcrumbs;
      return;
    }

    if (serverBreadcrumbs === null) {
      if (!isEqual(breadcrumbs, null)) {
        startTransition(() => {
          setBreadcrumbs(null);
        });
      }
      previousServerBreadcrumbsRef.current = serverBreadcrumbs;
      return;
    }

    const hasServerBreadcrumbsChanged = !isEqual(
      previousServerBreadcrumbsRef.current,
      serverBreadcrumbs,
    );
    if (hasServerBreadcrumbsChanged && serverBreadcrumbs && !serverBreadcrumbs.hasError) {
      startTransition(() => {
        setBreadcrumbs(serverBreadcrumbs);
      });
      previousServerBreadcrumbsRef.current = serverBreadcrumbs;
    }
  }, [serverBreadcrumbs, isMounted, getIssuerBreadcrumbsReq, query.companyViewId]);

  return {
    breadcrumbs,
    setBreadcrumbs,
    getIssuerBreadcrumbsReq,
  };
};

type BreadcrumbsContextType = ReturnType<typeof useBreadcrumbs>;

export const BreadcrumbsContext = createContext<BreadcrumbsContextType>(null);

export const useBreadcrumbsContext = () => {
  const context = useContext(BreadcrumbsContext);
  if (!context) {
    throw new Error('useBreadcrumbsContext must be used within the BreadcrumbsContext');
  }
  return context;
};

interface IProps {
  children: React.ReactNode,
  breadcrumbs?: IBreadcrumb[] | { hasError: boolean },
}

const BreadcrumbsContextProvider = ({ children, breadcrumbs }: IProps) => (
  <BreadcrumbsContext.Provider value={ useBreadcrumbs(breadcrumbs) }>
    { children }
  </BreadcrumbsContext.Provider>
);

export default BreadcrumbsContextProvider;
